import React, { useCallback, useMemo, useState } from 'react';

import { FormProvider } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { AccountFiltersDropdown } from '@/components/organisms/SessionsInfiniteTable/AccountFiltersDropdown';
import { FiltersBar } from '@/components/organisms/SessionsInfiniteTable/FiltersBar';
import { useAccountFilterViews } from '@/components/organisms/SessionsInfiniteTable/useAccountFilterViews';
import {
  AccountFilterFormValue,
  useAccountSessionFilters,
} from '@/components/organisms/SessionsInfiniteTable/useAccountSessionFilters';
import {
  AlertDialog,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { Button } from '@/components/ui/button';
import useBrains from '@/hooks/useBrains';
import useDesks from '@/hooks/useDesks';
import DateFilter from '@/modules/analytics/components/filters/DateFilter/DateFilter';
import useFilters from '@/modules/analytics/hooks/useFilters';
import { trackAnalyticsFilterApplied } from '@/modules/analytics/utils/analyticsTracking';
import useLanguageModels from '@/modules/developerTools/hooks/useLanguageModels';
import { AnalyticsType, LogsAnalytics } from '@/shared/types/analytics';
import { PageName } from '@/shared/types/segment';

interface AccountAnalyticsFiltersProps {
  /** Identifies the analytics page for engagement tracking. */
  page: PageName;
  allowedFilters?: string[];
  /** Shows only the date picker. */
  hideFilters?: boolean;
  rightActions?: React.ReactNode;
  exportActions?: React.ReactNode;
}

/**
 * Shared analytics filters component for all account analytics pages.
 * Uses the new filters dropdown UI similar to BrainLogsInfinite and AccountSessionsInfiniteTable.
 */
export const AccountAnalyticsFilters: React.FC<AccountAnalyticsFiltersProps> = ({
  page,
  allowedFilters,
  hideFilters = false,
  rightActions,
  exportActions,
}) => {
  const { t } = useTranslation();
  const { dateOpen, setDateOpen, filterOpen } = useFilters(AnalyticsType.ACCOUNT);
  const { desks } = useDesks();
  const { brains } = useBrains();
  const { languageModelNames } = useLanguageModels();
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);

  const {
    formMethods,
    fields,
    addFilter,
    removeFilter,
    saveFilters,
    filtersForDisplay,
    clearAllFilters,
    removeFilterByType,
    loadFilters,
    activeFilters,
  } = useAccountSessionFilters();

  const {
    views: savedViews,
    activeView,
    activeViewId,
    hasUnsavedChanges,
    isCheckingMatch,
    saveFilterView,
    loadFilterView,
    deleteFilterView,
    setActiveViewId,
  } = useAccountFilterViews<AccountFilterFormValue>(filtersForDisplay);

  const handleLoadView = useCallback(
    (viewId: string) => {
      const filters = loadFilterView(viewId);
      if (filters) {
        loadFilters(filters);
        const viewName = savedViews.find((view) => view.id === viewId)?.name;
        trackAnalyticsFilterApplied(page, 'saved_view', viewName);
      }
    },
    [loadFilterView, loadFilters, savedViews, page]
  );

  const handleSaveView = useCallback(
    async ({ name, overwriteExisting }: { name: string; overwriteExisting: boolean }) => {
      const overwriteId = overwriteExisting ? activeViewId : undefined;
      await saveFilterView(name, overwriteId);
    },
    [activeViewId, saveFilterView]
  );

  const handleUpdateActiveView = useCallback(async () => {
    if (activeView) {
      await saveFilterView(activeView.name, activeView.id);
    }
  }, [activeView, saveFilterView]);

  const handleDeleteView = useCallback(() => {
    setDeleteConfirmOpen(true);
  }, []);

  const handleDeleteViewConfirm = useCallback(async () => {
    if (activeView) {
      await deleteFilterView(activeView.id);
      clearAllFilters();
      setActiveViewId(undefined);
    }
    setDeleteConfirmOpen(false);
  }, [activeView, clearAllFilters, deleteFilterView, setActiveViewId]);

  const handleClearFilters = useCallback(() => {
    clearAllFilters();
    setActiveViewId(undefined);
  }, [clearAllFilters, setActiveViewId]);

  const handleRemoveFilter = useCallback(
    (filterType: string) => {
      if (filterType.startsWith('insightFilter-')) {
        const insightId = filterType.replace('insightFilter-', '');
        const insightFilter = activeFilters.find((filter) => filter.type === 'insightFilters');

        if (insightFilter?.insightFilters) {
          const remaining = insightFilter.insightFilters.filter(
            (insight) => insight.id !== insightId
          );

          if (remaining.length === 0 && activeFilters.length === 1) {
            setActiveViewId(undefined);
          }
        }
      } else if (activeFilters.length === 1) {
        setActiveViewId(undefined);
      }

      removeFilterByType(filterType);
    },
    [activeFilters, removeFilterByType, setActiveViewId]
  );

  // Filter filtersForDisplay (form values) to only show filters that are allowed on this page
  const displayedFilters = useMemo(() => {
    if (!allowedFilters || allowedFilters.length === 0) {
      return filtersForDisplay;
    }
    return filtersForDisplay.filter((filter) => {
      const filterType = filter.type;
      return allowedFilters.includes(filterType);
    });
  }, [filtersForDisplay, allowedFilters]);

  // Wraps the shared `saveFilters` so committing a filter change emits one
  // engagement event tagged with the page and the filter types that were
  // actually applied. `saveFilters` returns null when nothing changed (idle
  // Apply clicks, discarded drafts), and URL/localStorage restores go through
  // effects in useAccountSessionFilters, never this handler — so neither
  // inflates the numbers.
  const trackedSaveFilters = useCallback(() => {
    const applied = saveFilters();
    if (!applied) {
      return;
    }
    const filterTypes = applied.map((filter) => filter.type).join(',');
    trackAnalyticsFilterApplied(page, filterTypes || undefined);
  }, [page, saveFilters]);

  const handleDateApplied = useCallback(() => {
    trackAnalyticsFilterApplied(page, 'date_range');
  }, [page]);

  const filterIndexByType = useMemo(() => {
    return fields.reduce(
      (acc, field, index) => {
        const fieldType = field?.type;
        if (fieldType) {
          acc[fieldType] = index;
        }
        return acc;
      },
      {} as Record<string, number>
    );
  }, [fields]);

  const filtersBarVisible = !hideFilters && displayedFilters.length > 0;

  return (
    <FormProvider {...formMethods}>
      <div className="sticky top-0 z-30 w-full bg-subtle pt-4 pb-4 [&>*:last-child]:mb-0">
        <div className="mb-4 flex items-center justify-between">
          <DateFilter
            dateOpen={dateOpen}
            setDateOpen={setDateOpen}
            disabled={filterOpen}
            onDateApplied={handleDateApplied}
          />
          <div className="flex items-center gap-2">
            {rightActions}
            {!hideFilters && (
              <AccountFiltersDropdown
                formMethods={formMethods}
                fields={fields}
                addFilter={addFilter}
                removeFilter={removeFilter}
                saveFilters={trackedSaveFilters}
                activeFilters={displayedFilters}
                savedViews={savedViews}
                onLoadView={handleLoadView}
                allowedFilters={allowedFilters}
              />
            )}
            {exportActions}
          </div>
        </div>

        {filtersBarVisible && (
          <FiltersBar
            filters={displayedFilters as unknown as LogsAnalytics[]}
            onRemoveFilter={handleRemoveFilter}
            desks={desks}
            brains={brains}
            languageModelNames={languageModelNames}
            scope="account"
            onSaveView={handleSaveView}
            onUpdateActiveView={handleUpdateActiveView}
            onDeleteView={handleDeleteView}
            activeView={activeView}
            existingViews={savedViews}
            activeViewId={activeViewId}
            hasUnsavedChanges={hasUnsavedChanges}
            isCheckingMatch={isCheckingMatch}
            filterIndexByType={filterIndexByType}
            removeFilterAtIndex={removeFilter}
            onSaveFilters={trackedSaveFilters}
            onClearAll={handleClearFilters}
          />
        )}
      </div>

      <AlertDialog open={deleteConfirmOpen} onOpenChange={setDeleteConfirmOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>{t('brain_logs.delete_filter_view_title')}</AlertDialogTitle>
            <AlertDialogDescription>
              {t('brain_logs.delete_filter_view_description', { 0: activeView?.name })}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>{t('common.cancel')}</AlertDialogCancel>
            <Button onClick={handleDeleteViewConfirm} variant="destructive">
              {t('common.delete')}
            </Button>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </FormProvider>
  );
};
